import {
  type Density,
  DensityProvider,
  type Elevation,
  ElevationProvider,
  type SafeAreaPadding,
  type ThemeMode,
  initialSafeArea,
  useSafeArea,
} from '@glade-ai/ui';
import { createKeyborg } from 'keyborg';
import {
  type FC,
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { isInPopoverWidgetLauncher } from '@/helpers/helper';
import { useSupportsHover } from '@/hooks';

const defaultThemeMode: ThemeMode = 'light';

type UiContextValue = {
  themeMode: ThemeMode;
  setThemeMode: (nextThemeMode: ThemeMode) => void;
  hasIosKeyboard: boolean;
  safeAreaPadding?: SafeAreaPadding;
  /** Whether the device supports hover interactions (has a pointing device). */
  supportsHover: boolean;
};

const defaultContextValue: UiContextValue = {
  hasIosKeyboard: false,
  safeAreaPadding: initialSafeArea,
  setThemeMode: () => {},
  supportsHover: true,
  themeMode: defaultThemeMode,
};

/**
 * Detects if the device has an iOS on-screen keyboard.
 * Returns false during SSR when navigator is unavailable.
 */
const detectHasIosKeyboard = (): boolean => {
  // TODO(thure): UA sniffing is never good, however I haven't found a better way to query for whether the on-screen keyboard will appear on `focus` outside of a click handler.
  if (typeof navigator === 'undefined') return false;
  return /iP(ad|od|hone).+Safari/.test(navigator.userAgent);
};

const handleInputModalityChange = (isUsingKeyboard: boolean): void => {
  if (isUsingKeyboard) {
    document.body.setAttribute('data-is-keyboard', 'true');
  } else {
    document.body.removeAttribute('data-is-keyboard');
  }
};

/**
 * @internal
 */
const UiContext = createContext<UiContextValue>(defaultContextValue);

export const useUi = (): UiContextValue => useContext(UiContext);

type UiProviderProps = Partial<Omit<UiContextValue, 'safeAreaPadding'>> &
  PropsWithChildren<{
    rootDensity?: Density;
    rootElevation?: Elevation;
  }>;

const isThemeMode = (o: unknown): o is ThemeMode => typeof o === 'string' && ['light', 'dark'].includes(o);

export const UiProvider: FC<UiProviderProps> = ({
  children,
  rootDensity = 'fine',
  rootElevation = 'base',
  ...props
}) => {
  const [themeMode, setThemeModeInternal] = useState<ThemeMode>(defaultThemeMode);
  const [hasIosKeyboard, setHasIosKeyboard] = useState(false);
  const supportsHover = useSupportsHover();

  useEffect(() => {
    setHasIosKeyboard(detectHasIosKeyboard());
  }, []);

  const setThemeMode = useCallback((nextThemeMode: ThemeMode) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('glade-theme-mode', nextThemeMode);
    setThemeModeInternal(nextThemeMode);
    document.documentElement.classList.toggle('dark-mode', nextThemeMode === 'dark');
  }, []);

  useEffect(() => {
    const persistedThemeMode = localStorage.getItem('glade-theme-mode');
    if (isThemeMode(persistedThemeMode)) {
      setThemeMode(persistedThemeMode);
    } else if (localStorage.getItem('dashboard-dark-mode') === 'true') {
      setThemeMode('dark');
    } else {
      setThemeMode('light');
    }
  }, [setThemeMode]);

  // TODO(will): This is not how design system tokens are meant to work; move this into `tokens.css` or create a semantic token layer ASAP.
  /** @deprecated */
  const applyColorScheme = useCallback((appliedThemeMode: ThemeMode): void => {
    const root = document.documentElement;
    if (appliedThemeMode === 'dark') {
      root.style.setProperty('--color-gray-100', '#f0f0f0');
      root.style.setProperty('--color-gray-75', '#aaaaaa');
      root.style.setProperty('--color-gray-50', '#666666');
      root.style.setProperty('--color-gray-25', '#333333');
      root.style.setProperty('--color-gray-15', '#292929');
      root.style.setProperty('--color-gray-0', '#212121');
      root.style.setProperty('--color-error', '#e85160');
      root.style.setProperty(
        '--ai-bg-100',
        `conic-gradient(
          from 140deg at 50% 50%,
          rgba(239, 153, 1, 1) 0deg,
          rgba(50, 50, 50, 1) 120deg,
          rgba(50, 184, 249, 1) 230deg,
          rgba(50, 50, 50, 1) 300deg,
          rgba(239, 153, 1, 1) 360deg
        )`,
      );
      root.style.setProperty(
        '--ai-bg-20',
        `linear-gradient(22.82deg, rgba(50, 50, 50, 0.82) 1%, rgba(0, 0, 102, 0) 100%),
          linear-gradient(247.13deg, #212121 0%, rgba(0, 0, 102, 0) 100%),
          linear-gradient(158.35deg, rgba(255, 0, 85, 0) 0%, rgba(255, 222, 167, 0.99) 100%),
          conic-gradient(from 189.21deg at 0% 0%, #a809f6 22%, rgba(0, 163, 255, 0.99) 66%, #f60974 100%)`,
      );
    } else {
      root.style.removeProperty('--color-gray-100');
      root.style.removeProperty('--color-gray-75');
      root.style.removeProperty('--color-gray-50');
      root.style.removeProperty('--color-gray-25');
      root.style.removeProperty('--color-gray-15');
      root.style.removeProperty('--color-gray-0');
      root.style.removeProperty('--color-error');
      root.style.removeProperty('--ai-bg-100');
      root.style.removeProperty('--ai-bg-20');
    }
  }, []);

  useEffect(() => {
    applyColorScheme(themeMode);
    return () => {
      const root = document.documentElement;
      root.style.removeProperty('--color-gray-100');
      root.style.removeProperty('--color-gray-75');
      root.style.removeProperty('--color-gray-50');
      root.style.removeProperty('--color-gray-25');
      root.style.removeProperty('--color-gray-15');
      root.style.removeProperty('--color-gray-0');
      root.style.removeProperty('--color-error');
      root.style.removeProperty('--ai-bg-100');
      root.style.removeProperty('--ai-bg-20');
    };
  }, [applyColorScheme, themeMode]);

  // This effect replaces the former `DarkModeProvider` which only did this.
  useEffect(() => {
    if (isInPopoverWidgetLauncher()) {
      window.document.body.style.backgroundColor = 'transparent';
    } else {
      window.document.body.style.backgroundColor = 'var(--color-gray-0)';
    }
  }, []);

  useEffect(() => {
    const view = document.defaultView;
    if (!view) return undefined;

    const keyborg = createKeyborg(view);
    keyborg.subscribe(handleInputModalityChange);
    return () => keyborg.unsubscribe(handleInputModalityChange);
  }, []);

  const safeAreaPadding = useSafeArea();
  const contextValue = useMemo(
    () => ({
      ...props,
      hasIosKeyboard,
      safeAreaPadding,
      setThemeMode,
      supportsHover,
      themeMode,
    }),
    [hasIosKeyboard, props, safeAreaPadding, setThemeMode, supportsHover, themeMode],
  );
  return (
    <UiContext.Provider value={contextValue}>
      <ElevationProvider elevation={rootElevation}>
        <DensityProvider density={rootDensity}>{children}</DensityProvider>
      </ElevationProvider>
    </UiContext.Provider>
  );
};
